// pages/Contact.js
import React, { useState, useEffect } from 'react';
import './Contact.css';
import { FaArrowRight, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt, FaWhatsapp, FaClock, FaCheckCircle, FaUser, FaComment, FaPaperPlane } from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <FaEnvelope />,
      title: 'Email Us',
      details: ['hello@commex.pk', 'support@commex.pk'],
      link: 'mailto:hello@commex.pk',
      color: '#667eea'
    },
    {
      icon: <FaWhatsapp />,
      title: 'WhatsApp',
      details: ['+92 XXX XXXXXXX', 'Available 24/7'],
      link: 'https://wa.me/923000000000',
      color: '#25D366'
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Visit Us',
      details: ['Karachi, Pakistan', 'Sindh, PK'],
      link: '#',
      color: '#FF5F35'
    },
    {
      icon: <FaClock />,
      title: 'Business Hours',
      details: ['Mon - Fri: 9AM - 6PM', 'Sat: 10AM - 4PM'],
      link: null,
      color: '#764ba2'
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to build a website?',
      answer: 'We typically deliver a fully functional e-commerce website within 3-5 working days, depending on the complexity and number of products.'
    },
    {
      question: 'Do you offer payment plans?',
      answer: 'Yes, we offer flexible payment plans to help small businesses get started. Contact us for more details about our payment options.'
    },
    {
      question: 'Can I update my store myself after launch?',
      answer: 'Absolutely! We provide an easy-to-use admin panel that allows you to add products, update prices, and manage orders without any technical knowledge.'
    },
    {
      question: 'Do you provide hosting and domain?',
      answer: 'Yes, our packages include reliable hosting and domain registration. We ensure your website is fast, secure, and always online.'
    }
  ];

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-container">
          <h1 className="contact-hero-title animate-on-scroll">
            Let's Talk <span className="highlight">Business</span>
          </h1>
          <p className="contact-hero-subtitle animate-on-scroll">
            Have a question about our services? Need help with your online store?<br />
            We're here to help you succeed.
          </p>
        </div>
      </section>

      {/* Contact Info Cards - Similar to StoreOptions features grid */}
      <section className="contact-info-section">
        <div className="contact-container">
          <div className="contact-info-grid">
            {contactInfo.map((info, index) => (
              <div 
                key={index} 
                className={`info-card animate-on-scroll ${info.link ? 'clickable' : ''}`}
                onClick={() => info.link && window.open(info.link, '_blank')}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="info-icon-wrapper" style={{ background: info.color }}>
                  {info.icon}
                </div>
                <h3 className="info-title">{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx} className="info-detail">{detail}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map Section */}
      <section className="contact-form-section">
        <div className="contact-container">
          <div className="form-wrapper">
            <div className="form-header animate-on-scroll">
              <span className="section-badge">Get In Touch</span>
              <h2 className="section-title">We'd Love to Hear From You</h2>
              <p className="section-subtitle">
                Whether you have a question about our services, need a quote, or just want to say hello, we're ready to help.
              </p>
            </div>

            <div className="form-grid">
              {/* Contact Form */}
              <div className="contact-form-wrapper animate-on-scroll">
                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="name">
                        <FaUser className="input-icon" />
                        Full Name <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="email">
                        <FaEnvelope className="input-icon" />
                        Email Address <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="phone">
                        <FaPhoneAlt className="input-icon" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+92 XXX XXXXXXX"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="subject">
                        <FaComment className="input-icon" />
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="message">
                      <FaComment className="input-icon" />
                      Message <span className="required">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="6"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project or question..."
                      required
                    ></textarea>
                  </div>
                  
                  <button type="submit" className="submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>Sending... <div className="spinner"></div></>
                    ) : (
                      <>Send Message <FaPaperPlane /></>
                    )}
                  </button>
                  
                  {submitted && (
                    <div className="success-message">
                      <FaCheckCircle className="success-icon" />
                      <div>
                        <strong>Message Sent Successfully!</strong>
                        <p>We'll get back to you within 24 hours.</p>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Map / Location Section */}
              <div className="map-wrapper animate-on-scroll">
                <div className="map-card">
                  <h3 className="map-title">Find Us Here</h3>
                  <div className="map-container">
                    <iframe
                      title="Commex Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d231327.02770708596!2d66.98346312421144!3d24.921170245297994!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3eb33c5361ff85a5%3A0x2401821560d5e242!2sKarachi%2C%20Karachi%20City%2C%20Sindh%2C%20Pakistan!5e0!3m2!1sen!2s!4v1700000000000!5m2!1sen!2s"
                      width="100%"
                      height="250"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                  <div className="map-address">
                    <FaMapMarkerAlt className="address-icon" />
                    <div>
                      <strong>Commex Headquarters</strong>
                      <p>Karachi, Sindh, Pakistan</p>
                    </div>
                  </div>
                  <div className="map-cta">
                    <a href="#" className="map-directions-btn" target="_blank" rel="noopener noreferrer">
                      Get Directions <FaArrowRight />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="contact-container">
          <div className="faq-header animate-on-scroll">
            <span className="section-badge">FAQ</span>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Find quick answers to common questions about our services.
            </p>
          </div>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-card animate-on-scroll" style={{ animationDelay: `${index * 0.1}s` }}>
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="contact-cta">
        <div className="contact-container">
          <div className="cta-card animate-on-scroll">
            <div className="cta-content">
              <h3 className="cta-title">Ready to Launch Your Online Store?</h3>
              <p className="cta-text">
                Get started today and join hundreds of successful businesses using Commex.
              </p>
              <div className="cta-buttons">
                <a href="/demo" className="cta-primary" target="_blank" rel="noopener noreferrer">
                  View Demo Store <FaArrowRight />
                </a>
                <a href="#" className="cta-secondary" onClick={() => window.open('https://wa.me/923000000000', '_blank')}>
                  <FaWhatsapp /> Chat on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;